import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AMOUNTS = [1000, 2000, 5000, 10000];
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(n);

// « Recharger via Wave » : ouvre le lien Wave WÔMA puis crédit automatique (API) ou après vérification admin
export const WaveTopupButton = ({ user, defaultWallet = "credits", onDone, className = "", testId = "wave-topup-btn" }) => {
  const roles = user?.roles || [user?.role];
  const canSubscription = roles.includes("partner") || roles.includes("artisan");
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(1000);
  const [wallet, setWallet] = useState(canSubscription ? defaultWallet : "credits");
  const [phone, setPhone] = useState(user?.phone || "");
  const [pending, setPending] = useState(null);
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/credits/wave-topup`, { amount, wallet, payment_phone: phone });
      if (data.wave_payment_url) window.open(data.wave_payment_url, "_blank", "noopener");
      setPending({ id: data.transaction_id, auto: data.wave_mode === "api_auto" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Wave indisponible");
    } finally {
      setLoading(false);
    }
  };

  const declare = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/credits/confirm/${pending.id}`);
      toast.success(data.message);
      setOpen(false);
      setPending(null);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={`rounded-full bg-[#1DC8FF] hover:bg-[#12b5ea] text-[#0F172A] px-5 py-2.5 text-[11px] font-black uppercase tracking-widest active:scale-95 transition-transform ${className}`} data-testid={testId}>
        Recharger via Wave
      </button>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setPending(null); }}>
        <DialogContent className="bg-[#1A1A1A] border-white/10 text-white rounded-3xl max-w-md" data-testid="wave-topup-dialog">
          <DialogHeader>
            <DialogTitle className="font-black uppercase tracking-widest text-base">Recharger via Wave</DialogTitle>
            <DialogDescription className="text-white/60 text-sm">Paiement sécurisé sur la page Wave WÔMA de ARTEKNOS.</DialogDescription>
          </DialogHeader>
          {!pending ? (
            <>
              {canSubscription && (
                <div className="grid grid-cols-2 gap-2" data-testid="wave-wallet-choice">
                  {[["subscription", "Abonnement"], ["credits", "Crédits"]].map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setWallet(k)} className={`rounded-xl py-2 text-xs font-black transition-colors ${wallet === k ? "bg-[#D4AF37] text-[#0F172A]" : "bg-white/10 text-white"}`} data-testid={`wave-wallet-${k}`}>{l}</button>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-4 gap-2">
                {AMOUNTS.map((a) => (
                  <button key={a} type="button" onClick={() => setAmount(a)} className={`rounded-xl py-2 text-xs font-black transition-colors ${amount === a ? "bg-[#1DC8FF] text-[#0F172A]" : "bg-white/10 text-white"}`} data-testid={`wave-amount-${a}`}>{fmt(a)}</button>
                ))}
              </div>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Numéro Wave utilisé" className="bg-white/5 border-white/20 text-white" data-testid="wave-phone-input" />
              <button type="button" onClick={pay} disabled={loading} className="w-full rounded-full bg-[#1DC8FF] text-[#0F172A] py-3 font-black uppercase tracking-widest text-xs disabled:opacity-50" data-testid="wave-pay-btn">
                {loading ? "..." : `Payer ${fmt(amount)} FCFA avec Wave`}
              </button>
            </>
          ) : (
            <div className="space-y-3" data-testid="wave-pending-step">
              <p className="text-sm text-white/80">
                {pending.auto
                  ? "Terminez le paiement dans Wave : votre portefeuille sera crédité automatiquement et vous recevrez un WhatsApp."
                  : "Une fois le paiement effectué dans Wave, appuyez ci-dessous. Votre portefeuille est crédité dès vérification et vous recevez un WhatsApp."}
              </p>
              {!pending.auto && (
                <button type="button" onClick={declare} disabled={loading} className="w-full rounded-full bg-[#D4AF37] text-[#0F172A] py-3 font-black uppercase tracking-widest text-xs" data-testid="wave-declare-btn">
                  J'ai payé {fmt(amount)} FCFA
                </button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
